import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Input, Select, Button, message, Spin, Empty, Space } from 'antd';
import { ArrowRightOutlined, BankOutlined, EditOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { companiesApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const { Option } = Select;

const CompanyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/settings/companies', { state: buildGoBackState() });
  const goToDetail = () => {
    navigate(`/settings/companies/${id}`, {
      state: {
        returnTo: returnToRef.current,
        ...buildGoBackState(),
      },
    });
  };
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await companiesApi.getEditMeta(id);
        if (cancelled) return;
        const c = res?.company ?? res?.data?.company;
        if (!c) {
          setNotFound(true);
          return;
        }
        setTimezones(res?.timezones || []);
        setCurrencies(res?.currencies || []);
        const admin = res?.company_admin ?? res?.data?.company_admin;
        form.setFieldsValue({
          company_name: c.company_name,
          company_email: c.company_email,
          company_phone: c.company_phone,
          website: c.website,
          address: c.address,
          timezone: c.timezone,
          status: c.status,
          currency_id: c.currency_id,
          admin_name: admin?.name,
          admin_email: admin?.email,
        });
      } catch (error) {
        if (!cancelled) {
          message.error(error?.message || 'خطا در دریافت اطلاعات');
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, form]);

  const onFinish = async (values) => {
    const payload = {
      company_name: values.company_name?.trim(),
      company_email: values.company_email?.trim(),
      company_phone: values.company_phone?.trim() || null,
      website: values.website?.trim() || null,
      currency_id: values.currency_id,
      timezone: values.timezone,
      status: values.status,
      address: values.address?.trim() || '',
      name: values.admin_name?.trim(),
      email: values.admin_email?.trim(),
    };
    setSubmitting(true);
    try {
      await companiesApi.update(id, payload);
      message.success('اطلاعات شرکت بروزرسانی شد');
      goToDetail();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-900/25">
                <EditOutlined className="text-xl text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">ویرایش شرکت</h1>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={goToDetail}>
              جزئیات شرکت
            </Button>
          </div>
          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">{children}</div>
        </Card>
      </div>
    </MainLayout>
  );

  if (loading) {
    return shell(
      <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="py-24 text-center">
          <Spin size="large" />
        </div>
      </Card>,
    );
  }

  if (notFound) {
    return (
      <MainLayout>
        <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
          <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700">
                  <BankOutlined className="text-xl text-gray-600 dark:text-gray-300" />
                </div>
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">شرکت یافت نشد</h1>
              </div>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
                بازگشت به لیست
              </Button>
            </div>
            <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
              <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <Empty description="شرکت یافت نشد یا دسترسی ندارید" />
              </Card>
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return shell(
    <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <Form form={form} layout="vertical" onFinish={onFinish} className="max-w-full">
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <Form.Item name="company_name" label="نام شرکت" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="company_email" label="ایمیل شرکت" rules={[{ required: true, type: 'email' }]}>
            <Input dir="ltr" />
          </Form.Item>
          <Form.Item name="company_phone" label="تلفن شرکت">
            <Input dir="ltr" />
          </Form.Item>
          <Form.Item name="website" label="وب‌سایت">
            <Input dir="ltr" />
          </Form.Item>
          <Form.Item name="currency_id" label="ارز" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" popupClassName="custom-font">
              {currencies.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.currency_symbol} — {c.currency_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="timezone" label="منطقه زمانی" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" popupClassName="custom-font">
              {timezones.map((tz) => (
                <Option key={tz} value={tz}>
                  {tz}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
            <Select popupClassName="custom-font">
              <Option value="active">فعال</Option>
              <Option value="inactive">غیرفعال</Option>
              <Option value="license_expired">منقضی مجوز</Option>
            </Select>
          </Form.Item>
        </div>
        <Form.Item name="address" label="آدرس">
          <Input.TextArea rows={3} />
        </Form.Item>
        <div className="mt-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">مدیر شرکت</h3>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            <Form.Item name="admin_name" label="نام مدیر" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="admin_email" label="ایمیل مدیر" rules={[{ required: true, type: 'email' }]}>
              <Input dir="ltr" />
            </Form.Item>
          </div>
        </div>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">تغییر لوگو در این نسخه در دسترس نیست.</p>
        <Form.Item className="mb-0">
          <Space wrap>
            <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
              ذخیره تغییرات
            </Button>
            <Button onClick={goToDetail}>انصراف</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>,
  );
};

export default CompanyEdit;
